import { Google } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import { useAuthContext } from '@/context/AuthProvider';
import { useLogger } from '@/hooks/useLogger';
import { useTranslationPrefix } from '@/translations/i18n';

interface ContinueWithProviderButtonsProps {
  onError?: (error: string | null) => void;
}

export const ContinueWithProviderButtons: React.FC<ContinueWithProviderButtonsProps> = ({
  onError,
}) => {
  const { signInWithGoogle, signInWithApple, isLoggingIn } = useAuthContext();
  const [localError, setLocalError] = useState<string | null>(null);
  const t = useTranslationPrefix('sso_auth');
  const logger = useLogger('ContinueWithProviderButtons');

  const handleGoogleSignIn = async () => {
    try {
      setLocalError(null);
      if (onError) onError(null);
      await signInWithGoogle();
    } catch (error: any) {
      const errorMessage = error.message;
      setLocalError(errorMessage);
      if (onError) onError(errorMessage);
      logger.error('Google sign-in error:', error);
    }
  };

  // Login with Apple is currently commented out
  // Uncomment the following code when Apple sign-in is implemented
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAppleSignIn = async () => {
    try {
      setLocalError(null);
      if (onError) onError(null);
      await signInWithApple();
    } catch (error: any) {
      const errorMessage = error.message;
      setLocalError(errorMessage);
      if (onError) onError(errorMessage);
      logger.error('Apple sign-in error:', error);
    }
  };

  return (
    <Stack spacing={3}>
      {/* Tradehaus's work sign-on: an "or" rule, then a quiet tray button, so the lime primary stays the one action */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: 12,
          color: 'var(--tx-muted)',
          '&::before, &::after': { content: '""', flex: 1, height: '1px', background: 'var(--tx-line)' },
        }}>
        or
      </Box>
      <Box
        component="button"
        type="button"
        data-provider="google"
        onClick={handleGoogleSignIn}
        disabled={isLoggingIn}
        aria-label={t('login_with_google')}
        data-testid="sso-google-btn"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          height: 44,
          border: 0,
          borderRadius: '12px',
          background: 'var(--tx-fill)',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.28), inset 0 0 0 1px var(--tx-line)',
          color: 'var(--tx-text)',
          fontFamily: 'inherit',
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          cursor: 'pointer',
          transition: 'box-shadow 0.15s, transform 0.12s',
          '&:hover': { boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.28), inset 0 0 0 1px var(--tx-text)' },
          '&:active': { transform: 'scale(0.985)' },
          '&:disabled': { opacity: 0.5, cursor: 'default' },
        }}>
        <Google sx={{ fontSize: 18 }} />
        Continue with Google
      </Box>

      {localError && !onError && (
        <Typography variant="body2" color="error" textAlign="center">
          {localError}
        </Typography>
      )}
    </Stack>
  );
};
